import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { HeartHandshake, MessageCircle, Shield, Zap, Users, AlertTriangle, Star, RefreshCw, ArrowLeft, Share2, Download, Instagram, Link as LinkIcon, Mail, MessageSquare } from 'lucide-react';
import AppLogo from '../AppLogo';
import ShareableCareCard from './ShareableCareCard';
import ShareCardPreview from './ShareCardPreview';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toPng } from 'html-to-image';

const MetricCard = ({ icon: Icon, title, label, description, testid }) => (
  <div className="surface-card p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4 text-cyan-600" />
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{title}</p>
    </div>
    <p className="text-base font-bold text-foreground">{label}</p>
    {description && <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{description}</p>}
  </div>
);

const ProfileCard = ({ title, profiles, accent }) => (
  <div className={`rounded-2xl p-5 text-white ${accent}`} data-testid={`profile-card-${title.replace(/[^a-zA-Z]/g, '').toLowerCase()}`}>
    <p className="text-[11px] font-semibold uppercase tracking-wider opacity-80">{title}</p>
    {profiles.map(p => (
      <div key={p.domain} className="mt-1.5">
        <p className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.name}</p>
        <p className="text-xs opacity-90 leading-relaxed mt-0.5">{p.description}</p>
      </div>
    ))}
  </div>
);

export default function CareProfileResults({ results, onRetake, publicMode = false }) {
  const navigate = useNavigate();
  const [sharing, setSharing] = useState(false);
  const [generatingCard, setGeneratingCard] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const cardRef = useRef(null);
  const m = results.scores.metrics;
  const texts = results.scores.text_answers || {};

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await axios.post(`${API}/assessments/care_profile/share`);
      toast.success(`Care Guide shared! Emailed ${res.data.emails_sent} squad member${res.data.emails_sent !== 1 ? 's' : ''} and posted to your circle feed.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not share'); }
    setSharing(false);
  };

  // Share the quiz LINK itself (not the result card) so friends can take it too.
  const QUIZ_URL = `${window.location.origin}/care-profile-quiz`;
  const QUIZ_INVITE = `Just took the CircleUpSquad Vibe Check — a free quick-questions quiz on how you give & receive support. Try it: ${QUIZ_URL}`;
  const handleShareQuizNative = async () => {
    const data = { title: 'Vibe Check: Discover Your Care Profile', text: QUIZ_INVITE, url: QUIZ_URL };
    try {
      if (navigator.share) { await navigator.share(data); return; }
    } catch (e) { if (e?.name === 'AbortError') return; }
    // Fallback to copy
    try { await navigator.clipboard.writeText(QUIZ_URL); toast.success('Quiz link copied to clipboard!'); } catch { toast.error('Could not share — copy link manually.'); }
  };
  const handleCopyQuizLink = async () => {
    try { await navigator.clipboard.writeText(QUIZ_URL); toast.success('Quiz link copied!'); } catch { toast.error('Could not copy — long-press the link to share it.'); }
  };

  const buildCardBlob = async () => {
    if (!cardRef.current) return null;
    const dataUrl = await toPng(cardRef.current, {
      cacheBust: true, pixelRatio: 1, width: 1080, height: 1350,
    });
    const res = await fetch(dataUrl);
    return { blob: await res.blob(), dataUrl };
  };

  const primaryName = m.care_profile.primary?.[0]?.name || 'Care Profile';

  const handleDownloadCard = async () => {
    setGeneratingCard(true);
    try {
      const { dataUrl } = await buildCardBlob();
      const link = document.createElement('a');
      link.download = `my-care-profile-${primaryName.replace(/\s+/g, '-').toLowerCase()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success('Card downloaded! Post it to Instagram, Facebook, or wherever you share.');
    } catch (e) {
      toast.error('Could not generate image. Try again.');
    }
    setGeneratingCard(false);
  };

  const handleNativeShare = async () => {
    setGeneratingCard(true);
    try {
      const { blob } = await buildCardBlob();
      const file = new File([blob], `care-profile-${primaryName.replace(/\s+/g, '-').toLowerCase()}.png`, { type: 'image/png' });
      const shareData = {
        title: 'My Care Profile™',
        text: `I'm "${primaryName}" — discover your Care Profile at circleupsquad.com`,
        files: [file],
      };
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        handleDownloadCard();
        return;
      }
    } catch (e) {
      if (e.name !== 'AbortError') toast.error('Sharing not supported here — try Download.');
    }
    setGeneratingCard(false);
  };

  const join = (arr, key = 'name') => (arr || []).map(x => x[key]).join(' & ');

  return (
    <div className="min-h-screen pb-24 bg-background" data-testid="care-profile-results">
      {/* Off-screen shareable card (captured to PNG on demand) */}
      <div style={{ position: 'fixed', left: -99999, top: 0, pointerEvents: 'none' }} aria-hidden="true">
        <ShareableCareCard
          primary={m.care_profile.primary}
          secondary={m.care_profile.secondary}
          giving={m.caregiver_style.primary}
          ref={cardRef}
        />
      </div>

      <div className="surface-header">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="results-back-btn"><ArrowLeft className="w-5 h-5 text-muted-foreground" /></button>
            <AppLogo />
            <div>
              <h1 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Care Guide™</h1>
              <p className="text-xs text-muted-foreground">Share this with the people supporting you</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onRetake} data-testid="retake-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Retake</Button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-6 space-y-4">
        <ProfileCard title="Primary Care Profile™" profiles={m.care_profile.primary} accent="bg-gradient-to-br from-cyan-600 to-teal-600" />
        {m.care_profile.secondary?.length > 0 && (
          <ProfileCard title="Secondary Care Profile™" profiles={m.care_profile.secondary} accent="bg-gradient-to-br from-emerald-600 to-cyan-700" />
        )}

        <MetricCard icon={HeartHandshake} title="Caregiver Style™" testid="metric-caregiver-style"
          label={join(m.caregiver_style.primary)}
          description={m.caregiver_style.primary?.[0]?.description} />

        <MetricCard icon={Users} title="Care Gap™" testid="metric-care-gap"
          label={m.care_gap.top.gap > 2 ? `${m.care_gap.top.band} — +${m.care_gap.top.gap} in ${m.care_gap.top.label}` : m.care_gap.top.band}
          description={m.care_gap.top.description} />

        <div className="grid grid-cols-2 gap-3">
          <MetricCard icon={Zap} title="Care Intensity™" testid="metric-care-intensity"
            label={m.care_intensity.label} description={m.care_intensity.description} />
          <MetricCard icon={Shield} title="Autonomy Index™" testid="metric-autonomy"
            label={m.autonomy_index.label} description={m.autonomy_index.description} />
        </div>

        <MetricCard icon={Users} title="Social Energy™" testid="metric-social-energy"
          label={m.social_energy.label} description={m.social_energy.description} />

        <div className="surface-card p-4 border-l-4 border-amber-400" data-testid="metric-top-friction">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Top Care Friction™</p>
          </div>
          {m.top_friction.top.map(t => (
            <div key={t.id} className="mt-1">
              <p className="text-base font-bold text-foreground">{t.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Squad tip: {t.recommendation}</p>
            </div>
          ))}
        </div>

        <div className="surface-card p-4" data-testid="communication-summary">
          <div className="flex items-center gap-2 mb-2">
            <MessageCircle className="w-4 h-4 text-cyan-600" />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">How to Check In With Me</p>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div><p className="text-[10px] text-muted-foreground uppercase">Method</p><p className="font-medium text-foreground">{m.checkin_method.label || '—'}</p></div>
            <div><p className="text-[10px] text-muted-foreground uppercase">Best time</p><p className="font-medium text-foreground">{m.checkin_time.label || '—'}</p></div>
            <div><p className="text-[10px] text-muted-foreground uppercase">Frequency</p><p className="font-medium text-foreground">{m.checkin_frequency.label || '—'}</p></div>
            <div><p className="text-[10px] text-muted-foreground uppercase">Updates</p><p className="font-medium text-foreground">{m.update_sharing.label || '—'}</p></div>
          </div>
        </div>

        <div className="rounded-2xl p-4 bg-cyan-50 dark:bg-cyan-900/20" data-testid="current-priority">
          <div className="flex items-center gap-2 mb-1">
            <Star className="w-4 h-4 text-cyan-700 dark:text-cyan-300" />
            <p className="text-xs font-semibold text-cyan-700 dark:text-cyan-300 uppercase tracking-wide">This Week's Priority™</p>
          </div>
          <p className="text-lg font-bold text-cyan-900 dark:text-cyan-100">{m.current_priority.detail || m.current_priority.label || '—'}</p>
        </div>

        {Object.keys(texts).length > 0 && (
          <div className="surface-card p-4" data-testid="reflection-answers">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">In Their Own Words</p>
            <div className="space-y-3">
              {Object.values(texts).filter(t => t.question !== 'If "Something Else" — tell us what would help:').map((t) => (
                <div key={t.question}>
                  <p className="text-xs text-muted-foreground italic">{t.question}</p>
                  <p className="text-sm text-foreground mt-0.5">"{t.answer}"</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {publicMode ? (
          <>
            <div className="surface-card p-4 border-2 border-emerald-100 dark:border-emerald-900" data-testid="share-quiz-section-public">
              <div className="flex items-center gap-2 mb-1">
                <Share2 className="w-4 h-4 text-emerald-600" />
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Send the Vibe Check to a Friend</p>
              </div>
              <p className="text-sm text-muted-foreground mb-3">Know someone who{"'"}d love this? Send them the quiz — no login required.</p>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={handleShareQuizNative} data-testid="share-quiz-native-btn-public">
                  <Share2 className="w-4 h-4 mr-2" /> Share
                </Button>
                <a
                  href={`sms:?&body=${encodeURIComponent(QUIZ_INVITE)}`}
                  className="inline-flex items-center justify-center h-10 px-4 rounded-md border border-input bg-background hover:bg-accent text-sm font-medium"
                  data-testid="share-quiz-sms-btn-public"
                >
                  <MessageSquare className="w-4 h-4 mr-2" /> Text
                </a>
                <a
                  href={`mailto:?subject=${encodeURIComponent('Try this quick Care Profile quiz')}&body=${encodeURIComponent(QUIZ_INVITE)}`}
                  className="inline-flex items-center justify-center h-10 px-4 rounded-md border border-input bg-background hover:bg-accent text-sm font-medium"
                  data-testid="share-quiz-email-btn-public"
                >
                  <Mail className="w-4 h-4 mr-2" /> Email
                </a>
                <Button variant="outline" onClick={handleCopyQuizLink} data-testid="share-quiz-copy-btn-public">
                  <LinkIcon className="w-4 h-4 mr-2" /> Copy link
                </Button>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-cyan-500 via-cyan-600 to-emerald-500 text-white" data-testid="social-card-section-public">
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/15 blur-2xl" aria-hidden="true" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <Instagram className="w-5 h-5" />
                  <p className="text-[11px] font-semibold uppercase tracking-wider opacity-90">Instagram-Ready Card</p>
                </div>
                <h3 className="text-lg font-bold leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  I{"'"}m a {m?.care_profile?.primary?.[0]?.name || 'Care Profile'}
                </h3>
                <p className="text-xs opacity-90 mt-1">We generated a 1080×1350 image of your result — perfect for Instagram, Facebook, or Stories.</p>
                <button
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  className="mt-4 mx-auto block rounded-xl ring-2 ring-white/40 transition-transform hover:scale-[1.03] active:scale-[0.99]"
                  data-testid="share-card-preview-open-btn-public"
                >
                  <ShareCardPreview
                    primary={m.care_profile.primary}
                    secondary={m.care_profile.secondary}
                    giving={m.caregiver_style.primary}
                    width={180}
                    testid="share-card-preview-public"
                  />
                </button>
                <p className="text-[11px] text-center opacity-80 mt-2">Tap the card to see it full size</p>
                <Button
                  onClick={handleDownloadCard}
                  disabled={generatingCard}
                  className="w-full mt-4 bg-white text-cyan-700 hover:bg-cyan-50 font-semibold"
                  data-testid="share-card-download-btn-public"
                >
                  <Download className="w-4 h-4 mr-2" /> {generatingCard ? 'Making your card...' : 'Download my card'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleNativeShare}
                  disabled={generatingCard}
                  className="w-full mt-2 bg-transparent border-white/50 text-white hover:bg-white/15 hover:text-white"
                  data-testid="share-card-native-btn-public"
                >
                  <Share2 className="w-4 h-4 mr-2" /> Or share directly
                </Button>
              </div>
            </div>
            <Button className="btn-primary w-full" onClick={() => navigate('/auth')} data-testid="public-create-account-btn">
              Create My Free CircleUpSquad Account
            </Button>
            <Button variant="outline" className="w-full" onClick={() => navigate('/gift-intro')} data-testid="public-gift-btn">
              Gift CircleUpSquad to Someone
            </Button>
          </>
        ) : (
          <>
            <div className="surface-card p-4 border-2 border-emerald-100 dark:border-emerald-900" data-testid="share-quiz-section">
              <div className="flex items-center gap-2 mb-1">
                <Share2 className="w-4 h-4 text-emerald-600" />
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Send the Vibe Check to a Friend</p>
              </div>
              <p className="text-sm text-muted-foreground mb-3">Know someone who{"'"}d love this? Send them the quiz — no login required.</p>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={handleShareQuizNative} data-testid="share-quiz-native-btn">
                  <Share2 className="w-4 h-4 mr-2" /> Share
                </Button>
                <a href={`sms:?&body=${encodeURIComponent(QUIZ_INVITE)}`} className="inline-flex items-center justify-center h-10 px-4 rounded-md border border-input bg-background hover:bg-accent text-sm font-medium" data-testid="share-quiz-sms-btn">
                  <MessageSquare className="w-4 h-4 mr-2" /> Text
                </a>
                <a href={`mailto:?subject=${encodeURIComponent('Try this quick Care Profile quiz')}&body=${encodeURIComponent(QUIZ_INVITE)}`} className="inline-flex items-center justify-center h-10 px-4 rounded-md border border-input bg-background hover:bg-accent text-sm font-medium" data-testid="share-quiz-email-btn">
                  <Mail className="w-4 h-4 mr-2" /> Email
                </a>
                <Button variant="outline" onClick={handleCopyQuizLink} data-testid="share-quiz-copy-btn">
                  <LinkIcon className="w-4 h-4 mr-2" /> Copy link
                </Button>
              </div>
            </div>
            <div className="surface-card p-4 border-2 border-cyan-100 dark:border-cyan-900" data-testid="social-card-section">
              <div className="flex items-center gap-2 mb-1">
                <Instagram className="w-4 h-4 text-cyan-600" />
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Share Your Care Type</p>
              </div>
              <p className="text-sm text-muted-foreground mb-3">Get a beautiful image to post on Instagram, Facebook, or Stories.</p>
              <div className="flex items-center gap-3 mb-3">
                <button
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  className="rounded-xl transition-transform hover:scale-[1.03] active:scale-[0.99]"
                  data-testid="share-card-preview-open-btn"
                >
                  <ShareCardPreview
                    primary={m.care_profile.primary}
                    secondary={m.care_profile.secondary}
                    giving={m.caregiver_style.primary}
                    width={110}
                  />
                </button>
                <p className="text-xs text-muted-foreground leading-relaxed">This is exactly what you{"'"}ll post. Tap it to see it full size before you share.</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={handleNativeShare} disabled={generatingCard} data-testid="share-card-native-btn">
                  <Share2 className="w-4 h-4 mr-2" /> {generatingCard ? '...' : 'Share'}
                </Button>
                <Button variant="outline" onClick={handleDownloadCard} disabled={generatingCard} data-testid="share-card-download-btn">
                  <Download className="w-4 h-4 mr-2" /> {generatingCard ? '...' : 'Download'}
                </Button>
              </div>
            </div>
            <Button className="btn-primary w-full" onClick={handleShare} disabled={sharing} data-testid="share-care-guide-btn">
              <Share2 className="w-4 h-4 mr-2" /> {sharing ? 'Sharing...' : 'Share My Care Guide with My Squad'}
            </Button>
            <Button variant="outline" className="w-full" onClick={() => navigate('/dashboard')} data-testid="results-done-btn">Back to My Squad</Button>
          </>
        )}
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-sm" data-testid="share-card-preview-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Preview your card</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            <ShareCardPreview
              primary={m.care_profile.primary}
              secondary={m.care_profile.secondary}
              giving={m.caregiver_style.primary}
              width={260}
              testid="share-card-preview-large"
            />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button variant="outline" onClick={handleNativeShare} disabled={generatingCard} data-testid="preview-dialog-share-btn">
              <Share2 className="w-4 h-4 mr-2" /> {generatingCard ? '...' : 'Share'}
            </Button>
            <Button className="btn-primary" onClick={handleDownloadCard} disabled={generatingCard} data-testid="preview-dialog-download-btn">
              <Download className="w-4 h-4 mr-2" /> {generatingCard ? '...' : 'Download'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
